import { isMobile } from 'react-device-detect';
import styles from './style.module.css';
import { AES, enc } from 'crypto-js';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { FloatVideoCta } from '../../components/FloatVideoCta';
import { Loading } from '../../components/Loading';
import { Input } from '../../components/forms/Input';

import { HelmetComponent } from '../../components/Helmet';
import { useAuth } from '../../hooks/useAuth';

import { Notify } from '../../components/Notify';
import { StrongPassword } from '../../components/StrongPassword';
import * as routes from '../../constants/Routes';
import { api } from '../../server/api';

interface FormValues {
  password: string;
  confirmPassword: string;
}

export function PasswordRecuperation() {
  const { client, loadingClient, setClient } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const { email, clientId, time } = useParams();

  const [passwordValue, setPasswordValue] = useState('');

  useEffect(() => {
    if (!client) {
      const secretKey = 'oqdigital110420231543';
      const dencodeClientId = decodeURIComponent(clientId);
      const bytesClientId = AES.decrypt(dencodeClientId, secretKey);
      const originalTextClientId = bytesClientId.toString(enc.Utf8);
      getClientById(originalTextClientId);
    }
  }, []);

  async function getClientById(clientId) {
    try {
      const parameters = {
        userType: 'client',
        searchFunctionality: 'getClient',
        clientId,
      };

      const response = await api.post(`/search`, parameters);

      if (response.data.success) {
        setClient(response.data.data);
      }
    } catch (error) {
      console.log('error getClientById: ', error);
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  const handleForm = handleSubmit(async (data) => {
    if (data.password !== data.confirmPassword) {
      return Notify('As senhas são diferentes!', 'error');
    }

    const parameters = {
      userType: 'player',
      updateFunctionality: 'resetPassword',
      data: {
        ...data,
        email,
        clientId,
        time,
        pathnameRecuperation: location.pathname,
      },
    };

    try {
      const response = await api.post(`/update`, parameters);

      if (response.data.success) {
        navigate(routes.START);
        Notify(response.data.message, 'success');
      } else {
        Notify(response.data.message, 'error');
      }
    } catch (error) {
      Notify('Erro ao alterar senha', 'error');
    }
  });

  const renderInactiveClient = () => {
    return (
      <div className={styles.inactiveBox}>
        <i className={`material-icons-outlined ${styles.iconLogo}`}>
          domain_disabled
        </i>
        <p>Cliente inativo</p>
        <a href="https://oqdigital.com" className={styles.btnInactiveOutlined}>
          <i className="material-icons-outlined">arrow_back</i>
          Voltar ao site
        </a>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <form onSubmit={handleForm}>
        <div className="m-3">
          <Card isLarge={true} shadow={true}>
            <div className="mb-3">
              <p className="fs-4">Recuperar senha</p>
              <span>
                Recupere sua senha inserindo uma nova logo abaixo e confirmando.
              </span>
            </div>
            <div className="my-3">
              <StrongPassword password={passwordValue} />
            </div>
            <div className="mb-3">
              <Input
                type="password"
                placeholder="Crie uma senha"
                errors={errors.password ? 'Informe a senha!' : ''}
                {...register('password', {
                  required: true,
                  onChange: (e) => {
                    setPasswordValue(e.target.value);
                  },
                })}
                aria-invalid={errors.confirmPassword ? 'true' : 'false'}
              />
            </div>
            <div className="mb-3">
              <Input
                type="password"
                placeholder="Confirme a senha"
                errors={errors.password ? 'Confirme a senha' : ''}
                {...register('confirmPassword', {
                  required: true,
                })}
                aria-invalid={errors.password ? 'true' : 'false'}
              />
            </div>
            <div className="d-grid">
              <Button
                text={isSubmitting ? 'Aguarde...' : 'Criar nova senha'}
                icon="check"
                type="submit"
                color="primary"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              />
            </div>
          </Card>
        </div>
      </form>
    );
  };

  if (loadingClient) {
    return <Loading />;
  }

  return (
    <>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={'Alterar senha'}
      />
      <div
        style={{
          background: isMobile ? '' : `url(${client?.loginImage})`,
        }}
        className={`${styles.contentFull} ${styles.backgroundImage}`}
      >
        {client && client.urlVideoLogin && (
          <FloatVideoCta videoUrl={client.urlVideoLogin} />
        )}
        <div className={styles.contentLeft}>
          {isMobile && (
            <img
              className="img-fluid"
              src={client && client.loginImageMobile}
            />
          )}
        </div>
        <div className={styles.contentRight}>
          <div className={styles.centeredAllFlex}>
            {client && client.active ? renderForm() : renderInactiveClient()}
          </div>
        </div>
      </div>
    </>
  );
}
